import React, { Fragment, useEffect } from 'react'
import { ApolloProvider } from '@apollo/client'
import { GlobalStyle } from 'styles/global'
import client from 'api/client'
import Routes from 'routes'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DndProvider } from 'react-dnd'
import { initHotjar } from 'utils/ui-trackers/hotjar'
import { BrowserRouter } from 'react-router-dom'
import { LastLocationProvider } from 'react-router-last-location'
import LastMatchProvider from 'utils/last-match'
import 'core-js/features/promise'
import { ToastProvider } from 'context/toast/ToastContext'
import { ThemeColorsProvider } from '@arvoreeducacao/bonsai-ds'
import PlayerProvider from 'context/player'
import Player from 'components/player'
import { VoiceControlProvider } from 'context/voice-control'
import AccessibilityWidgets from 'components/accessibility-widgets'
import PostHogInitializer from 'utils/posthog/PostHogInitializer'
import dropUnactionableExceptions from 'utils/posthog/dropUnactionableExceptions'
import ClarityInitializer from 'utils/clarity/ClarityInitializer'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { GOOGLE_CLIENT_ID } from 'config/vars'
import './global.css'
import { PostHogProvider } from 'posthog-js/react'
import posthog from 'posthog-js'
import { initTelemetry } from 'utils/telemetry/telemetry'
import { SessionProvider } from 'components/session-provider'
import FloatingOnboardingButton from 'components/floating-onboarding-button'
import { OnboardingV3Provider } from 'context/onboarding-v3'
import OnboardingV3Orchestrator from 'components/onboarding-v3-orchestrator'
import FeatureTogglesHydrator from 'components/feature-toggles-hydrator'
import BiblionBranding from 'components/biblion-branding'
import ForceUpdateGate from 'components/force-update-gate'

const queryClient = new QueryClient()

const ArvoreApp = () => {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      initHotjar()

      posthog.init('phc_5Cn5BDeGUfo5EMj10DMTrqe1aZMlMxtvHhi8Gf6DCQw', {
        api_host: 'https://posthog.arvore.com.br',
        defaults: '2025-05-24',
        capture_exceptions: true,
        before_send: dropUnactionableExceptions
      })

      initTelemetry()
    }
  }, [])

  useEffect(() => {
    const isWebView = Boolean(
      typeof window !== 'undefined' &&
        (global.webViewActive || window.ReactNativeWebView)
    )

    if (isWebView && typeof document !== 'undefined') {
      document.body.classList.add('webview')
    }
  }, [])

  return (
    <PostHogProvider client={posthog}>
      <Fragment>
        <GlobalStyle />
        <DndProvider backend={HTML5Backend}>
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <QueryClientProvider client={queryClient}>
              <ApolloProvider client={client}>
                <AccessibilityWidgets />
                <PostHogInitializer />
                <ClarityInitializer />
                <SessionProvider />
                <FeatureTogglesHydrator />
                <BiblionBranding />
                <ForceUpdateGate />
                <ThemeColorsProvider>
                  <BrowserRouter>
                    <LastLocationProvider>
                      <LastMatchProvider>
                        <ToastProvider>
                          <PlayerProvider>
                            <VoiceControlProvider>
                              <Player />
                              <OnboardingV3Provider>
                                <Routes />
                                <FloatingOnboardingButton />
                                <OnboardingV3Orchestrator />
                              </OnboardingV3Provider>
                            </VoiceControlProvider>
                          </PlayerProvider>
                        </ToastProvider>
                      </LastMatchProvider>
                    </LastLocationProvider>
                  </BrowserRouter>
                </ThemeColorsProvider>
              </ApolloProvider>
            </QueryClientProvider>
          </GoogleOAuthProvider>
        </DndProvider>
      </Fragment>
    </PostHogProvider>
  )
}

export default ArvoreApp
